import {
  PerformanceDefaultOwner,
  PerformanceOwnershipFormItem,
  PerformanceRoleType,
} from './types';

export function buildDefaultPerformanceOwnershipItem(
  defaultOwner?: PerformanceDefaultOwner,
): PerformanceOwnershipFormItem {
  return {
    allocate_type: PerformanceRoleType.Staff,
    allocate_id: defaultOwner?.ownerId,
    owner_name: defaultOwner?.ownerName,
    ratio: 100,
    remark: undefined,
    is_default: true,
  };
}

export function syncDefaultPerformanceOwnershipItems(
  items: PerformanceOwnershipFormItem[] | undefined,
  defaultOwner?: PerformanceDefaultOwner,
): PerformanceOwnershipFormItem[] {
  const currentItems = items?.length
    ? [...items]
    : [buildDefaultPerformanceOwnershipItem(defaultOwner)];
  const [firstItem, ...restItems] = currentItems;

  return [
    {
      ...buildDefaultPerformanceOwnershipItem(defaultOwner),
      ...firstItem,
      allocate_type: PerformanceRoleType.Staff,
      allocate_id: defaultOwner?.ownerId,
      owner_name: defaultOwner?.ownerName,
      is_default: true,
    },
    ...restItems,
  ];
}

export function getPerformanceOwnershipRatioTotal(
  items: PerformanceOwnershipFormItem[] = [],
): number {
  return items.reduce((total, item) => {
    const ratio = Number(item?.ratio);
    return Number.isFinite(ratio) ? total + ratio : total;
  }, 0);
}

export function validatePerformanceOwnershipItems(
  items: PerformanceOwnershipFormItem[] | undefined,
): string | null {
  if (!items?.length) {
    return '请至少添加1个业绩归属';
  }

  const hasInvalidRatio = items.some((item) => {
    if (item?.ratio === null || item?.ratio === undefined) {
      return false;
    }
    return !Number.isFinite(Number(item.ratio));
  });

  if (hasInvalidRatio) {
    return '请输入正确的比例';
  }

  const hasDecimalRatio = items.some((item) => {
    if (item?.ratio === null || item?.ratio === undefined) {
      return false;
    }

    return !Number.isInteger(Number(item.ratio));
  });

  if (hasDecimalRatio) {
    return '比例只能填写整数';
  }

  const uniqueAllocations = new Set<string>();
  for (const item of items) {
    if (!item?.allocate_type || !item?.allocate_id) {
      continue;
    }

    const allocationKey = `${item.allocate_type}-${item.allocate_id}`;
    if (uniqueAllocations.has(allocationKey)) {
      return '归属人不能重复';
    }

    uniqueAllocations.add(allocationKey);
  }

  if (Math.abs(getPerformanceOwnershipRatioTotal(items) - 100) > 0.0001) {
    return '全部归属人比例合计必须为100%';
  }

  return null;
}

export function calculatePerformanceOwnershipAmountInCent(
  totalAmount: number | string | undefined,
  ratio: number | string | undefined,
): number {
  const amount = Number(totalAmount || 0);
  const nextRatio = Number(ratio || 0);

  if (!Number.isFinite(amount) || !Number.isFinite(nextRatio)) {
    return 0;
  }

  return Math.round((amount * nextRatio) / 100);
}

export function assignPerformanceOwnershipAmountsForSubmit<T extends PerformanceOwnershipFormItem>(
  items: T[] = [],
  totalAmount: number | string | undefined,
): Array<T & { amount: number }> {
  const normalizedTotalAmount = Number(totalAmount || 0);

  if (!items.length) {
    return [];
  }

  if (!Number.isFinite(normalizedTotalAmount)) {
    return items.map((item) => ({
      ...item,
      amount: 0,
    }));
  }

  const calculatedItems = items.map((item) => ({
    ...item,
    amount: calculatePerformanceOwnershipAmountInCent(normalizedTotalAmount, item?.ratio),
  }));
  const calculatedAmountTotal = calculatedItems.reduce((sum, item) => sum + item.amount, 0);

  if (calculatedAmountTotal === normalizedTotalAmount) {
    return calculatedItems;
  }

  const lastIndex = items.length - 1;
  let allocatedAmount = 0;

  return calculatedItems.map((item, index) => {
    const amount = index === lastIndex ? normalizedTotalAmount - allocatedAmount : item.amount;

    if (index !== lastIndex) {
      allocatedAmount += amount;
    }

    return {
      ...item,
      amount,
    };
  });
}

export function getFirstValidationErrorMessage(error: {
  errorFields?: Array<{ errors?: string[] }>;
}): string | undefined {
  return error?.errorFields?.find((item) => item?.errors?.length)?.errors?.[0];
}
